import { tabbable } from 'tabbable';
import { bool2str, KeyCode, forAll } from '../util';

/**
 * @typedef {Object} PopupOptions
 * @property {String} [PopupOptions.expandedAttribute]
 * @property {Boolean} [PopupOptions.addButtonListener]
 * @property {Boolean} [PopupOptions.closeOnOutsideClick]
 * @property {Boolean} [PopupOptions.disableScrolling]
 */

class Popup {
    /**
     * @param {Element} element
     * @param {PopupOptions} options
     */
    constructor(element, options = {}) {
        //default options values, overridden if non-empty options param
        const {
            expandedAttribute = 'aria-expanded',
            addButtonListener = true,
            closeOnOutsideClick = false,
            disableScrolling = true,
        } = options;
        this._options = {
            expandedAttribute,
            addButtonListener,
            closeOnOutsideClick,
            disableScrolling,
        };
        this._element = element;
        this._toggle = document.querySelector(`[aria-controls="${this._element.id}"]`);
        this._previouslyFocusedElement = null;
        this._onCloseClick = this._onCloseClick.bind(this);
        this._onOpenClick = this._onOpenClick.bind(this);
        this._onClick = this._onClick.bind(this);
        this._onKeyPress = this._onKeyPress.bind(this);
        this._addEventListeners();
    }

    get open() {
        return this._element.getAttribute(this._options.expandedAttribute) === 'true';
    }

    get focusables() {
        return tabbable(this._element);
    }

    set open(value) {
        if (value === this.open) {
            return;
        }
        this._element.setAttribute(this._options.expandedAttribute, bool2str(value));
        if (this._toggle)
            this._toggle.setAttribute(this._options.expandedAttribute, bool2str(value));
        const x = document.body.scrollLeft + document.documentElement.scrollLeft;
        const y = document.body.scrollTop + document.documentElement.scrollTop;
        let event;
        if (value) {
            if (this._options.disableScrolling) {
                document.body.style.overflow = 'hidden';
                document.body.scrollLeft = x;
                document.body.scrollTop = y;
                this._previouslyFocusedElement = document.activeElement;
                if (!this._element.contains(document.activeElement)) {
                    this.focusables[0].focus();
                }
            }
            event = new CustomEvent(this._options.expandedAttribute + '-open');
        } else {
            if (this._options.disableScrolling) {
                document.body.style.overflow = '';
                window.scrollTo(x, y);
            }
            event = new CustomEvent(this._options.expandedAttribute + '-close');
            if (this._previouslyFocusedElement) {
                this._previouslyFocusedElement.focus();
            }
        }
        this._element.dispatchEvent(event);
    }

    /**
     * @param {PointerEvent} e
     * @private
     */
    _onCloseClick(e) {
        this.open = false;
        e.preventDefault();
    }

    /**
     * @param {PointerEvent} e
     * @private
     */
    _onOpenClick(e) {
        this.open = true;
        e.preventDefault();
    }

    /**
     * @param {PointerEvent} e
     * @private
     */
    _onClick(ev) {
        if (this.open && ev.target === this._element && this._options.closeOnOutsideClick) {
            this.open = false;
            this._element.dispatchEvent(new CustomEvent('outside-click'));
        }
    }

    /**
     * @param {KeyboardEvent} e
     * @private
     */
    _onKeyPress(e) {
        if (this.open) {
            if (e.keyCode === KeyCode.ESCAPE) {
                this.open = false;
            }
            if (e.keyCode === KeyCode.TAB && !e.ctrlKey) {
                const focusable = this.focusables;
                if (!this._element.contains(document.activeElement)) {
                    focusable[0].focus();
                } else {
                    let current = null;
                    for (let i = 0; i < focusable.length; i++) {
                        if (focusable[i] === document.activeElement) {
                            current = i;
                            break;
                        }
                    }
                    if (current !== null) {
                        const i = (current + (e.shiftKey ? -1 : 1) + focusable.length) % focusable.length;
                        focusable[i].focus();
                    }
                }
                e.preventDefault();
            }

        }
    }

    _addEventListeners() {
        if (this._options.addButtonListener)
            this._element.querySelector('button').addEventListener('click', this._onCloseClick);
        if (this._toggle)
            this._toggle.addEventListener('click', this._onOpenClick);
        // this._element.addEventListener('click', this._onClick);
        window.addEventListener('click', this._onClick);
        window.addEventListener('keydown', this._onKeyPress);
    }

    static init() {
        forAll('.js-popup', e => new Popup(e));
    }
}

export {
    Popup,
};
